import React, { PropsWithChildren } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  SxProps,
  Typography,
  Theme,
  Box,
  IconButton,
} from "@mui/material";
import { colors } from "../../utils/constants";
import CreateUserIcon from "../../assets/CreateUserIcon.svg";
import CloseIcon from "@mui/icons-material/Close"; // ← Only this line added

export type DialogProps = {
  title?: string;
  cancelButton: () => void;
  actionButton?: () => void;
  actionButtonTitle?: string;
  hideActionButtons?: boolean;
  cancelButtonTitle?: string;
  minWidth?: number;
  disableActionButton?: boolean;
};

export default function CommonDialog({
  title,
  cancelButton,
  actionButton,
  actionButtonTitle = "Save",
  children,
  hideActionButtons = false,
  cancelButtonTitle = "Cancel",
  minWidth,
  disableActionButton = false,
}: PropsWithChildren<DialogProps>) {
  const onClickSaveBtn = () => {
    actionButton?.();
  };

  return (
    <Dialog
      open
      onClose={cancelButton}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={dialogStyles(minWidth)}
    >
      {title && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          mb={2}
          sx={dialogHeaderStyles}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <img
              src={CreateUserIcon}
              alt="Create User"
              style={{ height: 24 }}
            />
            <Typography fontSize={16}>{title}</Typography>
          </Box>
          <IconButton onClick={cancelButton} size="small">
            <CloseIcon fontSize="medium" color="action" />
          </IconButton>
        </Box>
      )}
      <DialogContent sx={dialogContentStyles}>{children}</DialogContent>
      {!hideActionButtons ? (
        <DialogActions
          id="dialog-action"
          sx={{ gap: 2, p: (theme) => theme.spacing(1, 2, 3, 2) }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={cancelButton}
            sx={outlinedButtonSx}
          >
            {cancelButtonTitle}
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={onClickSaveBtn}
            sx={containedButtonSx}
            disabled={disableActionButton}
          >
            {actionButtonTitle}
          </Button>
        </DialogActions>
      ) : null}
    </Dialog>
  );
}

const dialogStyles: (minWidth?: number) => SxProps<Theme> = (minWidth) => ({
  p: (theme) => theme.spacing(1, 2, 1, 2),
  ".MuiDialog-paper": {
    borderRadius: "4px",
    minWidth: minWidth ? minWidth : 400,
    maxWidth: 900,
  },
});

const dialogContentStyles: SxProps<Theme> = {
  ".MuiDialogContent-root&.MuiDialogContent-root": {
    // p: (theme) => theme.spacing(0, 0, 2, 2),
  },
};

const dialogHeaderStyles: SxProps<Theme> = {
  position: "relative",
  background: "#f5f5f5",
  boxShadow: "none",
  overflow: "hidden",
  height: "50px",
  paddingLeft: "16px",
  paddingRight: "16px",
  "@media (min-width:600px)": {
    paddingLeft: "24px",
    paddingRight: "24px",
  },

  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    height: "5px",
    width: "25%",
    background: `linear-gradient(to right, ${colors.indigoBlueColor} 0%, ${colors.indigoBlueColor} 30%,${colors.brownYellowColor} 30%, ${colors.brownYellowColor} 60%, ${colors.scarletColor} 60%, ${colors.scarletColor} 100%)`,
    borderTopRightRadius: "12px",
    borderBottomRightRadius: "12px",
  },
};

const containedButtonSx: SxProps = {
  textTransform: "inherit",
  backgroundColor: colors.indigoBlueColor,
  color: colors.white,
  padding: "8px",
  borderRadius: "8px",
};

const outlinedButtonSx: SxProps = {
  textTransform: "inherit",
  color: colors.indigoBlueColor,
  border: `0.5px solid ${colors.indigoBlueColor}`,
  padding: "8px",
  borderRadius: "8px",
};
